import { useState, useEffect, useMemo } from 'react';
import { getActiveProfile } from '../utils/connectorConfig';
import { formatDateTime } from '../utils/dateFormat';
import {
  Activity,
  Database,
  Shield,
  Boxes,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  Settings,
  Plus,
  Plug,
  RefreshCw,
  X,
  ClipboardList,
} from 'lucide-react';
import apiClient from '../services/api';
import axios from 'axios';
import useStore from '../store/useStore';

export default function Dashboard() {
  const { user, setConnectorFormOpen, connectorKey, theme } = useStore();
  const isLight = theme === 'light';
  const userRoles = user?.roles || [];
  const isProvider = userRoles.includes('data-provider') || userRoles.includes('admin') || userRoles.includes('realm-admin');
  const isConsumer = userRoles.includes('data-consumer') || userRoles.includes('admin') || userRoles.includes('realm-admin');

  const activeProfile = useMemo(() => getActiveProfile(), [connectorKey]);
  // Previously required BOTH providerEdcUrl and consumerEdcUrl for anyone
  // with both roles (which an Admin always has), so a connector set up as
  // provider-only (the common case - consumer DSP isn't something most
  // setups need) was permanently treated as "unconfigured" no matter what
  // was actually saved. Just require a connector to exist with at least
  // one side set up.
  const isConfigured = !!activeProfile &&
    (!!activeProfile.providerEdcUrl || !!activeProfile.consumerEdcUrl);

  const [stats, setStats] = useState({
    assets: 0, policies: 0, contracts: 0,
    completedTransfers: 0, successfulTransfers: 0, failedTransfers: 0
  });
  const [fetchingStats, setFetchingStats] = useState(false);
  const [apiStatus, setApiStatus] = useState({ provider: 'unconfigured', consumer: 'unconfigured' });
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [edcDetails, setEdcDetails] = useState(null);
  const [edcLoading, setEdcLoading] = useState(false);
  const [transferPage, setTransferPage] = useState(1);
  const TRANSFERS_PER_PAGE = 10;

  useEffect(() => {
    if (isConfigured) fetchStatistics();
  }, [connectorKey, isConfigured]);

  useEffect(() => {
    if (!selectedTransfer) { setEdcDetails(null); return; }
    // Skip secondary fetch when we already have rich data from the wrapper API
    if (selectedTransfer._rich) { setEdcDetails(null); return; }
    const fetchEdcDetails = async () => {
      setEdcLoading(true);
      setEdcDetails(null);
      const profile = getActiveProfile();
      const token = localStorage.getItem('kc_token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      for (const [baseUrl, apiKey] of [
        [profile?.providerEdcUrl, profile?.providerApiKey],
        [profile?.consumerEdcUrl, profile?.consumerApiKey],
      ]) {
        if (!baseUrl) continue;
        try {
          const res = await axios.post('/api/v1/edc-proxy', {
            targetUrl: baseUrl,
            path: `/api/management/v2/transferprocesses/${selectedTransfer.transfer_id}`,
            method: 'GET',
            headers: apiKey ? { 'X-Api-Key': apiKey } : {},
          }, { headers: authHeaders });
          if (res.data?.['@id']) { setEdcDetails(res.data); break; }
        } catch {}
      }
      setEdcLoading(false);
    };
    fetchEdcDetails();
  }, [selectedTransfer?.transfer_id]);

  const fetchStatistics = async () => {
    setFetchingStats(true);
    const profile = getActiveProfile();

    try {
      const token = localStorage.getItem('kc_token');
      const proxyHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // Assets count via EDC proxy using the active profile's provider URL
      if (isProvider && profile?.providerEdcUrl) {
        try {
          const assetsRes = await axios.post('/api/v1/edc-proxy', {
            targetUrl: profile.providerEdcUrl,
            path: '/api/management/v2/assets/request',
            method: 'POST',
            data: { '@context': {}, limit: 1000, offset: 0 },
            headers: profile.providerApiKey ? { 'X-Api-Key': profile.providerApiKey } : {}
          }, { headers: proxyHeaders });
          const count = Array.isArray(assetsRes.data) ? assetsRes.data.length : 0;
          setStats(prev => ({ ...prev, assets: count }));
          setApiStatus(prev => ({ ...prev, provider: 'connected' }));
        } catch {
          setApiStatus(prev => ({ ...prev, provider: 'error' }));
        }
      }

      // Consumer connectivity check via transfer history ping
      if (isConsumer && profile?.consumerEdcUrl) {
        try {
          await axios.post('/api/v1/edc-proxy', {
            targetUrl: profile.consumerEdcUrl,
            path: '/api/management/v2/transferprocesses/request',
            method: 'POST',
            data: { '@context': {}, limit: 1, offset: 0 },
            headers: profile.consumerApiKey ? { 'X-Api-Key': profile.consumerApiKey } : (profile.providerApiKey ? { 'X-Api-Key': profile.providerApiKey } : {}),
          }, { headers: proxyHeaders });
          setApiStatus(prev => ({ ...prev, consumer: 'connected' }));
        } catch {
          setApiStatus(prev => ({ ...prev, consumer: 'error' }));
        }
      }

      // Policies count via EDC proxy
      if (isProvider && profile?.providerEdcUrl) {
        try {
          const policiesRes = await axios.post('/api/v1/edc-proxy', {
            targetUrl: profile.providerEdcUrl,
            path: '/api/management/v2/policydefinitions/request',
            method: 'POST',
            data: { '@context': {}, limit: 1000, offset: 0 },
            headers: profile.providerApiKey ? { 'X-Api-Key': profile.providerApiKey } : {}
          }, { headers: proxyHeaders });
          const count = Array.isArray(policiesRes.data) ? policiesRes.data.length : 0;
          setStats(prev => ({ ...prev, policies: count }));
        } catch { /* ignore */ }
      }

      // Contract definitions count via EDC proxy
      if (isProvider && profile?.providerEdcUrl) {
        try {
          const contractsRes = await axios.post('/api/v1/edc-proxy', {
            targetUrl: profile.providerEdcUrl,
            path: '/api/management/v2/contractdefinitions/request',
            method: 'POST',
            data: { '@context': {}, limit: 1000, offset: 0 },
            headers: profile.providerApiKey ? { 'X-Api-Key': profile.providerApiKey } : {}
          }, { headers: proxyHeaders });
          const count = Array.isArray(contractsRes.data) ? contractsRes.data.length : 0;
          setStats(prev => ({ ...prev, contracts: count }));
        } catch { /* ignore */ }
      }

      // Transfer stats + recent records
      let automationStats = { completed: 0, successful: 0, failed: 0 };
      let manualStats = { initiated: 0, failed: 0 };

      try {
        const jobsResponse = await apiClient.get('/automation/jobs');
        if (Array.isArray(jobsResponse.data)) {
          automationStats.completed = jobsResponse.data.reduce((s, j) => s + (j.run_count || 0), 0);
          automationStats.successful = jobsResponse.data.reduce((s, j) => s + (j.success_count || 0), 0);
          automationStats.failed = jobsResponse.data.reduce((s, j) => s + (j.error_count || 0), 0);
        }
      } catch { /* ignore */ }

      // Fetch transfer history from both connectors, combining results
      const allTransfers = [];
      const seenIds = new Set();

      const fetchFromConnector = async (edcUrl, edcApiKey) => {
        if (!edcUrl) return;
        // Try sovity wrapper first — returns rich pre-shaped data
        try {
          const wRes = await axios.post('/api/v1/edc-proxy', {
            targetUrl: edcUrl,
            path: '/api/management/wrapper/ui/pages/transfer-history-page',
            method: 'GET',
            headers: edcApiKey ? { 'X-Api-Key': edcApiKey } : {},
          }, { headers: proxyHeaders });
          const items = wRes.data?.transferEntries || wRes.data?.transfers || (Array.isArray(wRes.data) ? wRes.data : null);
          if (Array.isArray(items) && items.length > 0) {
            items.forEach(t => {
              if (!seenIds.has(t.transferProcessId)) {
                seenIds.add(t.transferProcessId);
                allTransfers.push({
                  transfer_id: t.transferProcessId,
                  asset_id: t.assetId || '—',
                  asset_name: t.assetName || t.assetId || '—',
                  contractAgreementId: t.contractAgreementId || null,
                  counterPartyConnectorEndpoint: t.counterPartyConnectorEndpoint || null,
                  counterPartyParticipantId: t.counterPartyParticipantId || null,
                  created_at: t.createdDate || null,
                  last_updated: t.lastUpdatedDate || null,
                  direction: t.direction || '—',
                  state: t.state || null,
                  status: t.state?.name || '—',
                  _rich: true,
                });
              }
            });
            return;
          }
        } catch { /* wrapper not available */ }
        // Fallback to standard EDC API
        try {
          const tpRes = await axios.post('/api/v1/edc-proxy', {
            targetUrl: edcUrl,
            path: '/api/management/v2/transferprocesses/request',
            method: 'POST',
            data: { '@context': {}, limit: 100, offset: 0 },
            headers: edcApiKey ? { 'X-Api-Key': edcApiKey } : {},
          }, { headers: proxyHeaders });
          if (Array.isArray(tpRes.data)) {
            tpRes.data.forEach(t => {
              const id = t['@id'];
              if (!seenIds.has(id)) {
                seenIds.add(id);
                allTransfers.push({
                  transfer_id: id,
                  asset_id: t['edc:assetId'] || t.assetId || '—',
                  contractAgreementId: t['edc:contractId'] || t['edc:contractAgreementId'] || null,
                  counterPartyConnectorEndpoint: t['edc:counterPartyAddress'] || null,
                  counterPartyParticipantId: t['edc:counterPartyId'] || null,
                  status: t['edc:state'] || t.state || '—',
                  created_at: t['edc:createdAt'] ? new Date(t['edc:createdAt']).toISOString() : null,
                  last_updated: t['edc:stateTimestamp'] ? new Date(t['edc:stateTimestamp']).toISOString() : null,
                  direction: t['edc:type'] === 'PROVIDER' ? 'PROVIDING' : t['edc:type'] === 'CONSUMER' ? 'CONSUMING' : (t['edc:type'] || '—'),
                  _rich: false,
                });
              }
            });
          }
        } catch { /* ignore */ }
      };

      await fetchFromConnector(profile?.consumerEdcUrl, profile?.consumerApiKey || profile?.providerApiKey);

      if (allTransfers.length > 0) {
        allTransfers.sort((a, b) => new Date(b.last_updated || b.created_at || 0) - new Date(a.last_updated || a.created_at || 0));
        setRecentTransfers(allTransfers);
        manualStats.initiated = allTransfers.filter(t => t.state?.simplifiedState === 'OK' || t.status === 'COMPLETED').length;
        manualStats.failed = allTransfers.filter(t => t.state?.simplifiedState === 'ERROR' || ['ERROR', 'TERMINATED'].includes(t.status)).length;
      }

      setStats(prev => ({
        ...prev,
        completedTransfers: automationStats.completed + manualStats.initiated,
        successfulTransfers: automationStats.successful + manualStats.initiated,
        failedTransfers: automationStats.failed + manualStats.failed
      }));
    } catch { /* ignore */ } finally {
      setFetchingStats(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':    return <CheckCircle className="text-green-400" size={20} />;
      case 'error':        return <AlertTriangle className="text-red-400" size={20} />;
      case 'unconfigured': return <Clock className="text-gray-400" size={20} />;
      default:             return <Clock className="text-yellow-400 animate-pulse" size={20} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':    return 'Connected';
      case 'error':        return 'Connection Error';
      case 'unconfigured': return 'Not Configured';
      default:             return 'Checking...';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold font-display text-white mb-2">Dashboard</h1>
        <p className="text-gray-400">Overview of your ENACT Dataspace</p>
      </div>

      {/* Connector panel */}
      {isConfigured ? (
        <div className="card bg-green-900/10 border-green-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <CheckCircle className="text-green-400 flex-shrink-0" size={22} />
            <div className="min-w-0">
              <h3 className="font-semibold text-green-400 truncate">{activeProfile.name} — connected</h3>
              <p className="text-sm text-gray-400">Your EDC connector is set up and active</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => fetchStatistics()} disabled={fetchingStats}
              className="btn btn-secondary flex items-center gap-2 flex-shrink-0">
              <RefreshCw size={15} className={fetchingStats ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button onClick={() => setConnectorFormOpen(true)}
              className="btn btn-secondary flex items-center gap-2 flex-shrink-0">
              <Plus size={15} />
              Add another connector
            </button>
          </div>
        </div>
      ) : (
        <div className="card bg-blue-900/10 border-blue-800 text-center py-10">
          <div className="w-16 h-16 rounded-2xl bg-blue-600/20 border border-blue-700 flex items-center justify-center mx-auto mb-4">
            <Plug className="text-blue-400" size={28} />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Connect your first connector</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Add a name, pick a colour, and enter your EDC management API URL to get started.
          </p>
          <button onClick={() => setConnectorFormOpen(true)}
            className="btn btn-primary inline-flex items-center gap-2 mx-auto">
            <Plus size={16} />
            Set up connector
          </button>
        </div>
      )}

      {/* Connection Status cards */}
      {isConfigured && (isProvider || isConsumer) && (
        <div className={`grid grid-cols-1 gap-6 ${isProvider && isConsumer ? 'md:grid-cols-2' : ''}`}>
          {isProvider && (
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Provider EDC</p>
                  <p className="text-lg font-semibold mt-1">{getStatusText(apiStatus.provider)}</p>
                </div>
                {getStatusIcon(apiStatus.provider)}
              </div>
            </div>
          )}
          {isConsumer && (
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Consumer EDC</p>
                  <p className="text-lg font-semibold mt-1">{getStatusText(apiStatus.consumer)}</p>
                </div>
                {getStatusIcon(apiStatus.consumer)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats grid */}
      {isConfigured ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <Database className="text-blue-400" size={24} />
                <TrendingUp className="text-green-400" size={20} />
              </div>
              <p className="text-3xl font-bold text-white mb-2">{fetchingStats ? '…' : stats.assets}</p>
              <p className="text-gray-400 text-sm">Data Assets</p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <Shield className="text-green-400" size={24} />
                <TrendingUp className="text-green-400" size={20} />
              </div>
              <p className="text-3xl font-bold text-white mb-2">{fetchingStats ? '…' : stats.policies}</p>
              <p className="text-gray-400 text-sm">Access Policies</p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <Boxes className="text-purple-400" size={24} />
                <Activity className="text-blue-400" size={20} />
              </div>
              <p className="text-3xl font-bold text-white mb-2">{fetchingStats ? '…' : stats.contracts}</p>
              <p className="text-gray-400 text-sm">Contract Definitions</p>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <Activity className="text-yellow-400" size={24} />
                <TrendingUp className="text-green-400" size={20} />
              </div>
              <p className="text-3xl font-bold text-white mb-2">{fetchingStats ? '…' : stats.completedTransfers}</p>
              <p className="text-gray-400 text-sm">Completed Transfers</p>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Transfer History</h2>
              {recentTransfers.length > 0 && (
                <span className="text-xs text-gray-400">{recentTransfers.length} total</span>
              )}
            </div>
            {recentTransfers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Activity className="mx-auto mb-3 opacity-40" size={40} />
                <p className="text-sm">No transfer records yet</p>
              </div>
            ) : (() => {
              const totalPages = Math.ceil(recentTransfers.length / TRANSFERS_PER_PAGE);
              const pageItems = recentTransfers.slice(
                (transferPage - 1) * TRANSFERS_PER_PAGE,
                transferPage * TRANSFERS_PER_PAGE
              );
              return (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800 text-left">
                          <th className="pb-3 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Direction</th>
                          <th className="pb-3 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Last Updated</th>
                          <th className="pb-3 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Asset</th>
                          <th className="pb-3 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">State</th>
                          <th className="pb-3 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Counterparty ID</th>
                          <th className="pb-3 pr-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Counterparty Endpoint</th>
                          <th className="pb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        {pageItems.map((t) => {
                          const stateLabel = t.state?.name || t.status || '—';
                          const simplified = t.state?.simplifiedState;
                          const stateColor = simplified === 'OK' || stateLabel === 'COMPLETED'
                            ? (isLight ? 'bg-green-100 text-green-700' : 'bg-green-900/40 text-green-400')
                            : simplified === 'ERROR' || stateLabel === 'TERMINATED' || stateLabel === 'ERROR'
                            ? (isLight ? 'bg-red-100 text-red-700' : 'bg-red-900/40 text-red-400')
                            : (isLight ? 'bg-blue-100 text-blue-700' : 'bg-blue-900/40 text-blue-400');
                          const lastUp = t.last_updated || t.created_at;
                          const timeAgo = lastUp ? (() => {
                            const diff = Date.now() - new Date(lastUp).getTime();
                            const d = Math.floor(diff / 86400000);
                            const h = Math.floor(diff / 3600000);
                            const m = Math.floor(diff / 60000);
                            return d > 0 ? `${d}d ago` : h > 0 ? `${h}h ago` : `${m}m ago`;
                          })() : '—';
                          return (
                            <tr key={t.transfer_id} className="hover:bg-gray-800/40 transition-colors">
                              <td className="py-3 pr-4 text-gray-300 text-xs">{t.direction || '—'}</td>
                              <td className="py-3 pr-4 text-gray-400 text-xs whitespace-nowrap">{timeAgo}</td>
                              <td className="py-3 pr-4 text-gray-300 text-xs truncate max-w-[120px]">{t.asset_id || '—'}</td>
                              <td className="py-3 pr-4">
                                <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${stateColor}`}>
                                  {stateLabel}
                                </span>
                              </td>
                              <td className="py-3 pr-4 text-gray-300 text-xs truncate max-w-[120px]">{t.counterPartyParticipantId || '—'}</td>
                              <td className="py-3 pr-4 text-gray-300 text-xs truncate max-w-[160px]">{t.counterPartyConnectorEndpoint || '—'}</td>
                              <td className="py-3">
                                <button
                                  onClick={() => setSelectedTransfer(t)}
                                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors whitespace-nowrap"
                                >
                                  Show Details
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 mt-2 border-t border-gray-800">
                      <p className="text-xs text-gray-400">
                        Page {transferPage} of {totalPages}
                        <span className="ml-2 text-gray-500">
                          ({(transferPage - 1) * TRANSFERS_PER_PAGE + 1}–{Math.min(transferPage * TRANSFERS_PER_PAGE, recentTransfers.length)} of {recentTransfers.length})
                        </span>
                      </p>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setTransferPage(1)}
                          disabled={transferPage === 1}
                          className="btn btn-secondary px-2 py-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        >«</button>
                        <button
                          onClick={() => setTransferPage(p => Math.max(1, p - 1))}
                          disabled={transferPage === 1}
                          className="btn btn-secondary px-2 py-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        >‹</button>
                        <button
                          onClick={() => setTransferPage(p => Math.min(totalPages, p + 1))}
                          disabled={transferPage === totalPages}
                          className="btn btn-secondary px-2 py-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        >›</button>
                        <button
                          onClick={() => setTransferPage(totalPages)}
                          disabled={transferPage === totalPages}
                          className="btn btn-secondary px-2 py-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        >»</button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </>
      ) : (
        <div className="card bg-gray-800/30 border-gray-700 text-center py-12">
          <Settings className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">No connector active</h3>
          <p className="text-gray-500">Set up a connector above to view statistics</p>
        </div>
      )}

      {selectedTransfer && (() => {
        const t = selectedTransfer;
        const d = edcDetails;
        const assetId           = t.asset_id || d?.['edc:assetId'] || '—';
        const assetName         = t.asset_name || assetId;
        const contractId        = t.contractAgreementId || d?.['edc:contractAgreementId'] || d?.['edc:contractId'] || '—';
        const endpoint          = t.counterPartyConnectorEndpoint || d?.['edc:counterPartyAddress'] || d?.['edc:counterPartyConnectorAddress'] || '—';
        const participantId     = t.counterPartyParticipantId || d?.['edc:counterPartyId'] || '—';
        const rawCreated        = t.created_at || (d?.['edc:createdAt'] ? new Date(d['edc:createdAt']).toISOString() : null);
        const createdDate       = formatDateTime(rawCreated);
        const direction         = t.direction || (d?.['edc:type'] === 'PROVIDER' ? 'PROVIDING' : d?.['edc:type'] === 'CONSUMER' ? 'CONSUMING' : '—');
        const lastUpdated       = formatDateTime(t.last_updated);
        const stateName         = t.state?.name || d?.['edc:state'] || t.status?.toUpperCase() || '—';
        const stateCode         = t.state?.code ?? '—';
        const simplifiedState   = t.state?.simplifiedState || (stateName === 'COMPLETED' ? 'OK' : stateName === 'ERROR' || stateName === 'FAILED' ? 'ERROR' : stateName);
        const transferProcessId = t.transfer_id || d?.['@id'];

        const Field = ({ label, value }) => (
          <div className="grid grid-cols-[auto_1fr] gap-x-4">
            <span className="text-sky-400 text-xs whitespace-nowrap">{label}</span>
            <span className="text-gray-100 text-xs font-mono break-all">{value}</span>
          </div>
        );

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedTransfer(null)}>
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-800">
                <div className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <ClipboardList size={17} className="text-gray-300" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-white truncate">{assetId}</h2>
                  <p className="text-xs text-gray-400">Transfer History Details</p>
                </div>
                <button onClick={() => setSelectedTransfer(null)} className="ml-auto text-gray-400 hover:text-white transition-colors flex-shrink-0">
                  <X size={18} />
                </button>
              </div>

              <div className="px-6 py-4 overflow-y-auto space-y-2.5 flex-1">
                {edcLoading ? (
                  <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                    <RefreshCw size={15} className="animate-spin mr-2" />
                    Loading details…
                  </div>
                ) : (
                  <>
                    <Field label="assetId"                       value={assetId} />
                    <Field label="assetName"                     value={assetName} />
                    <Field label="contractAgreementId"           value={contractId} />
                    <Field label="counterPartyConnectorEndpoint" value={endpoint} />
                    <Field label="counterPartyParticipantId"     value={participantId} />
                    <Field label="createdDate"                   value={createdDate} />
                    <Field label="direction"                     value={direction} />
                    <Field label="lastUpdatedDate"               value={lastUpdated} />
                    <div className="grid grid-cols-[auto_1fr] gap-x-4">
                      <span className="text-sky-400 text-xs whitespace-nowrap">state</span>
                      <div className="space-y-1.5 pl-2">
                        <div className="grid grid-cols-[auto_1fr] gap-x-4">
                          <span className="text-sky-300 text-xs whitespace-nowrap">code</span>
                          <span className="text-gray-100 text-xs font-mono">{stateCode}</span>
                        </div>
                        <div className="grid grid-cols-[auto_1fr] gap-x-4">
                          <span className="text-sky-300 text-xs whitespace-nowrap">name</span>
                          <span className="text-gray-100 text-xs font-mono">{stateName}</span>
                        </div>
                        <div className="grid grid-cols-[auto_1fr] gap-x-4">
                          <span className="text-sky-300 text-xs whitespace-nowrap">simplifiedState</span>
                          <span className="text-gray-100 text-xs font-mono">{simplifiedState}</span>
                        </div>
                      </div>
                    </div>
                    <Field label="transferProcessId" value={transferProcessId} />
                  </>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-800 flex justify-end">
                <button onClick={() => setSelectedTransfer(null)} className="btn btn-secondary text-sm">Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
